<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue'
    import { useRoute } from 'vue-router'

    import AdminNavigationV2 from "@common/AdminNavigationV2.vue"
    import ValidationError from "@common/Components/ValidationError.vue"

    import type { WorkspaceReadModel } from "@Organizations/Workspaces/readmodels"

    import { useCurrentUserQueryV1 } from "@Organizations/Users/queries-vue-composables"
    import { useWorkspacesByOrganizationQueryV1 } from "@Organizations/Workspaces/queries-vue-composables"
    import { useCreateWorkspaceCommandV1 } from "@Organizations/Workspaces/commands-vue-composables"

    // ---------------- routing ----------------

    const route = useRoute()
    const tid = computed(() => Number(route.params.tid ?? 0))

    // ---------------- state ----------------

    const name = ref('')
    const description = ref('')
    const startupUrl = ref('')
    const workspaceCreated = ref(false)

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        document.title = 'Create a new workspace'
        runCurrentUserQueryV1()
    })

    // ---------------- queries ----------------

    const { runCurrentUserQueryV1 } = useCurrentUserQueryV1(false, false, true)

    const {
        modelWorkspacesByOrganizationQueryV1: workspacesModel,
        runWorkspacesByOrganizationQueryV1
    } = useWorkspacesByOrganizationQueryV1(tid)

    const workspaces = computed<WorkspaceReadModel[]>(() => workspacesModel.value?.Workspaces ?? [])

    const createdWorkspaceId = computed(() =>
        workspaces.value.find(w => w.Name === name.value)?.WorkspaceId
    )

    // ---------------- command ----------------

    const {
        invokeCreateWorkspaceCommandV1,
        invokingCreateWorkspaceCommandV1,
        succeedCreateWorkspaceCommandV1,
        validationErrorsCreateWorkspaceCommandV1
    } = useCreateWorkspaceCommandV1(
        () => '0',
        tid,
        name,
        description,
        startupUrl
    )

    const validationErrors = validationErrorsCreateWorkspaceCommandV1

    watch(succeedCreateWorkspaceCommandV1, succeed =>
    {
        if (succeed)
        {
            runWorkspacesByOrganizationQueryV1()
            workspaceCreated.value = true
        }
    })

    // ---------------- actions ----------------

    function handleCreateWorkspaceCommandV1()
    {
        if (invokingCreateWorkspaceCommandV1.value)
            return

        invokeCreateWorkspaceCommandV1()
    }

    function handleFinish()
    {
        name.value = ''
        description.value = ''
        startupUrl.value = ''
        workspaceCreated.value = false
    }
</script>

<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid" />
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Create a new workspace</h1>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="!workspaceCreated" class="container">
            <div class="form-group">
                <label>Name:</label>
                <input v-model="name" class="form-control" placeholder="Name of workspace" />
                <ValidationError context="Name" :errors="validationErrors" />
            </div>

            <div class="form-group">
                <label>Description:</label>
                <textarea v-model="description" class="form-control" placeholder="Description of workspace" />
                <ValidationError context="Description" :errors="validationErrors" />
            </div>

            <div class="form-group">
                <label>Startup url:</label>
                <input v-model="startupUrl" class="form-control" placeholder="Startup url - only for advanced scenarios" />
                <ValidationError context="StartupUrl" :errors="validationErrors" />
            </div>

            <button v-show="!invokingCreateWorkspaceCommandV1"
                    @click="handleCreateWorkspaceCommandV1"
                    class="btn btn-primary btn-lg">
                Create
            </button>

            <button v-show="invokingCreateWorkspaceCommandV1"
                    disabled
                    class="btn btn-primary btn-lg">
                Creating...
            </button>
        </div>

        <div v-else class="container">
            <h4 class="fg-color-blue">Workspace was created</h4>

            <div v-if="createdWorkspaceId">
                <a :href="`/#/admin/organizations/workspaces/manage/${createdWorkspaceId}`"
                   class="btn btn-success text-white"
                   style="padding: 0.7em 2em;">
                    Manage workspace members
                </a>

                <h4 class="fg-color-blue" style="margin: 20px 0;">or</h4>

            </div>

            <button @click="handleFinish"
                    class="btn btn-secondary text-white"
                    style="padding: 0.7em 2em;">
                Add another workspace
            </button>
        </div>
    </div>
</template>
